import React, { useEffect, useMemo, useState } from "react";
import { FaRegCopy } from "react-icons/fa6";

import "./PromptDiff.css";
import { useToastDispatchContext } from '/src/layouts/toast/context/toastContext';

function diffWords(oldText, newText) {
    const oldTokens = (oldText || "").split(/(\s+)/).filter(Boolean);
    const newTokens = (newText || "").split(/(\s+)/).filter(Boolean);
    const n = oldTokens.length;
    const m = newTokens.length;

    const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            dp[i][j] = oldTokens[i] === newTokens[j]
                ? dp[i + 1][j + 1] + 1
                : Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
    }

    const parts = [];
    let i = 0, j = 0;
    while (i < n && j < m) {
        if (oldTokens[i] === newTokens[j]) {
            parts.push({ type: "same", value: oldTokens[i] });
            i++; j++;
        } else if (dp[i + 1][j] >= dp[i][j + 1]) {
            parts.push({ type: "removed", value: oldTokens[i] });
            i++;
        } else {
            parts.push({ type: "added", value: newTokens[j] });
            j++;
        }
    }
    while (i < n) { parts.push({ type: "removed", value: oldTokens[i] }); i++; }
    while (j < m) { parts.push({ type: "added", value: newTokens[j] }); j++; }

    return parts;
}

function PromptDiff({ oldText, newText }) {
    const toast = useToastDispatchContext();
    const [debouncedNewText, setDebouncedNewText] = useState(newText);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedNewText(newText), 300);
        return () => clearTimeout(timer);
    }, [newText]);

    const parts = useMemo(() => diffWords(oldText, debouncedNewText), [oldText, debouncedNewText]);
    const { added, removed } = useMemo(() => parts.reduce((acc, part) => {
        if (part.type === "added") acc.added += 1;
        if (part.type === "removed") acc.removed += 1;
        return acc;
    }, { added: 0, removed: 0 }), [parts]);

    const copyLastSaved = async () => {
        await navigator.clipboard.writeText(oldText || "");
        toast.success("Copied", "Last saved prompt copied to clipboard.");
    };

    return (
        <div className="prompt-diff">
            <div className="prompt-diff-legend">
                <span className="diff-legend-item added">+{added} ajout{added > 1 ? "s" : ""}</span>
                <span className="diff-legend-item removed">-{removed} suppression{removed > 1 ? "s" : ""}</span>
                <span className="diff-copy-btn" onClick={copyLastSaved}>
                    <FaRegCopy />&nbsp;Copy last saved
                </span>
            </div>
            <div className="prompt-diff-body">
                {
                    parts.map((part, index) => {
                        if (part.type === "same") return <React.Fragment key={index}>{part.value}</React.Fragment>;
                        return (
                            <span key={index} className={`diff-token ${part.type}`}>
                                {part.value}
                            </span>
                        );
                    })
                }
            </div>
        </div>
    );
}

export default PromptDiff;
